import React, { useLayoutEffect, useMemo, useState } from "react";
import cn from "classnames";
import style from "./common.module.scss";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { useTranslation } from "react-i18next";
import { useScreenSize } from "../../../hooks/useScreenSize";
import {
  formatDateWithDay,
  getAmChartsLocale,
  processOrderCountData,
  shouldShowYear,
} from "../../../utils/chartDataProcessors";
import { useLanguage } from "../../../context/LanguageContext";
import GroupByDropdown from "./GroupByDropdown/GroupByDropdown";
import OrderStatusDropdown from "./GroupByDropdown/OrderStatusDropdown";
import SelectCurrencyOverlay from "./SelectCurrencyOverlay/SelectCurrencyOverlay";

const OrderCountByPeriod = ({ item, isExpeditor = false, isCompleted = true, setIsCompleted, currency }) => {
  const { isMobileView } = useScreenSize();
  const { currentLanguage } = useLanguage();
  const { t } = useTranslation();
  const [groupBy, setGroupBy] = useState("day");

  const processedData = useMemo(() => {
    const fallbackDate = new Date();

    const safeItem =
      Array.isArray(item) && item.length > 0
        ? item
        : [
          {
            createdAt: fallbackDate.toISOString(),
            count: 0,
          },
        ];

    const transformedData = safeItem.map((dataItem) => ({
      ...dataItem,
      date: new Date(dataItem.createdAt).toLocaleDateString("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }),
      count: dataItem.count || 0,
    }));

    if (groupBy === "day") {
      const showYear = shouldShowYear(transformedData);

      return transformedData
        .map((dataItem) => {
          const [day, month, year] = dataItem.date.split(".");
          const isoDate = new Date(year, month - 1, day);

          return {
            ...dataItem,
            month: formatDateWithDay(isoDate, currentLanguage, showYear),
            tooltipText: formatDateWithDay(isoDate, currentLanguage, showYear).replace("\n", " "),
            isoDate,
            count: dataItem.count || 0,
          };
        })
        .sort((a, b) => a.isoDate - b.isoDate);
    } else {
      return processOrderCountData(transformedData, currentLanguage, ["count"]);
    }
  }, [item, currentLanguage, groupBy]);

  useLayoutEffect(() => {
    const root = am5.Root.new("order-count-by-period");
    root.setThemes([am5themes_Animated.new(root)]);
    root._logo?.dispose();

    const locale = getAmChartsLocale(currentLanguage);
    if (locale) {
      root.locale = locale;
    }

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        layout: root.verticalLayout,
        wheelY: "zoomX",
        panX: true,
        panY: false,
        zoomX: true,
      }),
    );

    if (isMobileView) {
      chart.set("paddingLeft", 1);
    }

    const cursor = chart.set(
      "cursor",
      am5xy.XYCursor.new(root, {
        behavior: "zoomX",
      }),
    );
    cursor.lineY.set("visible", false);

    const xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: "category",
        groupData: true,
        renderer: am5xy.AxisRendererX.new(root, {
          minGridDistance: groupBy === "day" ? 60 : 40,
          cellStartLocation: 0.1,
          cellEndLocation: 0.9,
        }),
        tooltip: am5.Tooltip.new(root, {
          labelText: groupBy === "day" ? "{category}" : "{category}",
        }),
      }),
    );

    xAxis.get("renderer").grid.template.setAll({
      strokeDasharray: [4, 4],
      strokeOpacity: 1,
      stroke: am5.color(0xe7e7f4),
    });

    if (groupBy === "day") {
      xAxis.get("renderer").labels.template.setAll({
        fill: am5.color("#8E8E8E"),
        fontSize: 12,
        rotation: 0,
        multiline: true,
        textAlign: "center",
        centerX: am5.percent(50),
        maxWidth: 70,
        oversizedBehavior: "wrap",
      });
    } else {
      xAxis.get("renderer").labels.template.setAll({
        fill: am5.color("#8E8E8E"),
        fontSize: 12,
        rotation: 0,
        multiline: true,
        textAlign: "center",
        centerX: am5.percent(50),
        maxWidth: 50,
        oversizedBehavior: "wrap",
      });
    }

    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, {}),
        min: 0, // Ensure Y-axis starts from 0
      }),
    );

    yAxis.get("renderer").grid.template.setAll({
      strokeDasharray: [4, 4],
      strokeOpacity: 1,
      stroke: am5.color(0xe7e7f4),
    });

    yAxis.get("renderer").labels.template.setAll({
      fill: am5.color("#8E8E8E"),
      fontSize: 12,
    });

    // Define month order for consistent x-axis
    const monthsOrder = {
      'ru': ['янв.', 'февр.', 'мар.', 'апр.', 'май', 'июн.', 'июл.', 'авг.', 'сент.', 'окт.', 'нояб.', 'дек.'],
      'en': ['Jan.', 'Feb.', 'Mar.', 'Apr.', 'May', 'Jun.', 'Jul.', 'Aug.', 'Sep.', 'Oct.', 'Nov.', 'Dec.']
    };

    const currentMonthsOrder = monthsOrder[currentLanguage] || monthsOrder['ru'];

    const dataByYear = {};
    processedData.forEach((dataItem) => {
      const year = dataItem.isoDate
        ? dataItem.isoDate.getFullYear()
        : new Date(dataItem.date.split(".").reverse().join("-")).getFullYear();
      if (!dataByYear[year]) {
        dataByYear[year] = [];
      }
      dataByYear[year].push({
        category: dataItem.month,
        tooltipText: dataItem.tooltipText || dataItem.month,
        count: dataItem.count,
        originalMonth: dataItem.isoDate ? dataItem.isoDate.getMonth() : new Date(dataItem.date.split(".").reverse().join("-")).getMonth()
      });
    });

    // Colors for different years
    const yearColors = {
      2023: "#FF6B6B",
      2024: "#22C0FF",
      2025: "#4E7CFF",
      2026: "#4ECDC4",
    };

    if (groupBy === "day") {
      // Day grouping - show ALL data points
      const allDayData = [];

      processedData.forEach(item => {
        const date = item.isoDate || new Date(item.createdAt);
        const monthNames = currentLanguage === 'en'
          ? ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
          : ['янв.', 'февр.', 'мар.', 'апр.', 'май', 'июн.', 'июл.', 'авг.', 'сент.', 'окт.', 'нояб.', 'дек.'];

        const monthName = monthNames[date.getMonth()];
        const day = date.getDate();
        const year = date.getFullYear();
        const monthDay = `${monthName} ${day}`;

        allDayData.push({
          category: monthDay,
          year: year.toString(),
          count: item.count,
          date: date,
          tooltipText: `${monthDay} ${year}`,
          originalData: item
        });
      });

      // Sort all data chronologically first by month, then by day
      allDayData.sort((a, b) => {
        const aMonth = a.date.getMonth();
        const bMonth = b.date.getMonth();
        if (aMonth !== bMonth) return aMonth - bMonth;
        return a.date.getDate() - b.date.getDate();
      });

      // Create unique categories in chronological order
      const uniqueCategories = [];
      const categorySet = new Set();

      allDayData.forEach(item => {
        if (!categorySet.has(item.category)) {
          categorySet.add(item.category);
          uniqueCategories.push({
            category: item.category,
            sortDate: item.date
          });
        }
      });

      xAxis.data.setAll(uniqueCategories);

      // Group data by year
      const dayDataByYear = {};
      allDayData.forEach(item => {
        if (!dayDataByYear[item.year]) {
          dayDataByYear[item.year] = [];
        }
        dayDataByYear[item.year].push(item);
      });

      const sortedYears = Object.keys(dayDataByYear).sort();

      sortedYears.forEach((year) => {
        const color = yearColors[year] || "#999999";

        // Create data for this year's series - SHOW ALL DATA POINTS
        const seriesData = uniqueCategories.map(categoryItem => {
          const dataPoint = dayDataByYear[year]?.find(item =>
            item.category === categoryItem.category
          );

          return {
            category: categoryItem.category,
            count: dataPoint ? dataPoint.count : null,
            originalCount: dataPoint ? dataPoint.count : 0,
            tooltipText: dataPoint ? dataPoint.tooltipText : categoryItem.category
          };
        });

        const series = chart.series.push(
          am5xy.LineSeries.new(root, {
            name: `${year}`,
            xAxis: xAxis,
            yAxis: yAxis,
            valueYField: "count",
            categoryXField: "category",
            fill: am5.color(color),
            stroke: am5.color(color),
            tooltip: am5.Tooltip.new(root, {
              labelText: `{tooltipText}: {originalCount}`,
            }),
          }),
        );

        series.strokes.template.setAll({
          strokeWidth: 3,
        });

        // Bullets - SHOW ALL POINTS including zeros
        series.bullets.push(function (root, series, dataItem) {
          const circle = am5.Circle.new(root, {
            radius: 4,
            fill: am5.color(color),
            strokeWidth: 2,
          });

          return am5.Bullet.new(root, {
            sprite: circle,
          });
        });

        // Value labels - SHOW ALL VALUES including zeros
        series.bullets.push(function (root, series, dataItem) {
          return am5.Bullet.new(root, {
            sprite: am5.Label.new(root, {
              text: "{originalCount}",
              fill: am5.color(0x000000),
              centerY: am5.p100,
              centerX: am5.p50,
              populateText: true,
              fontSize: 11,
              fontWeight: "500"
            }),
          });
        });

        series.data.setAll(seriesData);
      });

      // Legend for day grouping
      if (sortedYears.length > 1) {
        const legend = chart.children.push(
          am5.Legend.new(root, {
            centerX: am5.p50,
            x: am5.p50,
          }),
        );
        legend.data.setAll(chart.series.values);
      }
    } else {
      // Month grouping - show ALL data points
      const availableMonths = new Set();

      // Process ALL data points to find available months
      processedData.forEach(item => {
        // Get the actual date to determine the month
        const date = item.isoDate || new Date(item.createdAt);
        const monthIndex = date.getMonth();
        const monthName = currentMonthsOrder[monthIndex];

        if (monthName) {
          availableMonths.add(monthName);
        }

        // Also check the processed month name as fallback
        const cleanMonth = item.month
          ?.replace(/\s*\d{4}\s*г?\.?\s*$/i, '')
          ?.replace(/\s*\d{4}\s*year\s*$/i, '')
          ?.replace(/\s*\d{4}\s*$/i, '')
          ?.trim();
        if (cleanMonth && currentMonthsOrder.includes(cleanMonth)) {
          availableMonths.add(cleanMonth);
        }
      });

      // Use ALL months that have data, maintaining chronological order
      const orderedAvailableMonths = currentMonthsOrder.filter(month =>
        availableMonths.has(month)
      );

      xAxis.data.setAll(orderedAvailableMonths.map(month => ({
        category: month
      })));

      // Create mapping from processed data directly
      const createMonthMapping = (processedData, orderedMonths) => {
        const mapping = {};

        // Initialize mapping with ordered months
        orderedMonths.forEach(month => {
          mapping[month] = {};
        });

        // Process each data item directly
        processedData.forEach(item => {
          const date = item.isoDate || new Date(item.createdAt);
          const year = date.getFullYear().toString();
          const monthIndex = date.getMonth();
          const monthName = currentMonthsOrder[monthIndex];

          if (monthName && mapping[monthName] !== undefined) {
            // Aggregate counts for the same month/year
            if (!mapping[monthName][year]) {
              mapping[monthName][year] = {
                count: 0,
                tooltipText: monthName
              };
            }
            mapping[monthName][year].count += item.count;
          }
        });

        return mapping;
      };

      const monthMapping = createMonthMapping(processedData, orderedAvailableMonths);

      // Get all years that have data
      const allYears = new Set();
      processedData.forEach(item => {
        const date = item.isoDate || new Date(item.createdAt);
        allYears.add(date.getFullYear().toString());
      });
      const sortedYears = Array.from(allYears).sort();

      sortedYears.forEach((year) => {
        const color = yearColors[year] || "#999999";

        // Create data for this year's series - SHOW ALL DATA POINTS
        const seriesData = orderedAvailableMonths
          .map(month => {
            const monthData = monthMapping[month];
            const yearData = monthData ? monthData[year] : null;

            if (!yearData || yearData.count === 0) {
              return {
                category: month,
                count: null, // Use null for missing data points
                originalCount: 0,
                tooltipText: month
              };
            }

            return {
              category: month,
              count: yearData ? yearData.count : 0, // Show 0 instead of null
              originalCount: yearData ? yearData.count : 0,
              tooltipText: yearData ? yearData.tooltipText : month
            };
          });

        const series = chart.series.push(
          am5xy.LineSeries.new(root, {
            name: `${year}`,
            xAxis: xAxis,
            yAxis: yAxis,
            valueYField: "count",
            categoryXField: "category",
            fill: am5.color(color),
            stroke: am5.color(color),
            tooltip: am5.Tooltip.new(root, {
              labelText: `{name}: {originalCount}`,
            }),
          }),
        );

        series.strokes.template.setAll({
          strokeWidth: 3,
        });

        // Bullets - SHOW ALL POINTS including zeros
        series.bullets.push(function (root, series, dataItem) {
          const circle = am5.Circle.new(root, {
            radius: 4,
            fill: am5.color(color),
            strokeWidth: 2,
          });

          return am5.Bullet.new(root, {
            sprite: circle,
          });
        });

        // Value labels - SHOW ALL VALUES including zeros
        series.bullets.push(function (root, series, dataItem) {
          return am5.Bullet.new(root, {
            sprite: am5.Label.new(root, {
              text: "{originalCount}",
              fill: am5.color(0x000000),
              centerY: am5.p100,
              centerX: am5.p50,
              populateText: true,
              fontSize: 11,
              fontWeight: "500"
            }),
          });
        });

        series.data.setAll(seriesData);
      });

      // Legend for month grouping
      if (sortedYears.length > 1) {
        const legend = chart.children.push(
          am5.Legend.new(root, {
            centerX: am5.p50,
            x: am5.p50,
          }),
        );
        legend.data.setAll(chart.series.values);
      }
    }

    return () => root.dispose();
  }, [processedData, isMobileView, currentLanguage, groupBy]);

  return (
    <div className={style.content}>
      <div className={style.filter}>
        <span className={style.filter_title}>{isExpeditor ? t("analytics.orderTurnover") : t("analytics.orderCountByPeriod")}</span>
        <div style={{ display: "flex", gap: "10px" }}>
          {isExpeditor && <OrderStatusDropdown isCompleted={isCompleted} setIsCompleted={setIsCompleted} />}
          <GroupByDropdown groupBy={groupBy} setGroupBy={setGroupBy} />
        </div>
      </div>
      <div className={style.area} style={{ position: "relative" }}>
        <SelectCurrencyOverlay show={currency === "all" || currency === ""} />
        <div id="order-count-by-period" className={style.area_chart}></div>
      </div>
    </div>
  );
};

export default OrderCountByPeriod;